import React, { useState, useEffect } from "react";
import axios from "axios";
import "./styles.css";
import Alert from "@mui/material/Alert";

const STORAGE_KEY = "ytdpl_display_mode";
const OPTIONS_PROFIT_KEY = "ytdpl_options_profit";
const PRIVACY_MODE_KEY = "ytdpl_privacy_mode";
const USER_KEY_STORAGE = "userKey"; // Added for consistency

const Printdebug = true;
const printdebug = true;
const debugprefix = "DDD";

const ChevronIcon = ({ size = 20, color = "#00b4d8" }) => (
    <svg
        width={size}
        height={size}
        viewBox="0 0 20 20"
        fill="none"
        style={{ verticalAlign: "middle" }}
        xmlns="http://www.w3.org/2000/svg"
    >
        <polyline
            points="6 8 10 12 14 8"
            stroke={color}
            strokeWidth="2"
            fill="none"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
    </svg>
);

const TooltipIcon = ({ tooltip, size = 16 }) => {
    const [showTooltip, setShowTooltip] = useState(false);
    const [tooltipPos, setTooltipPos] = useState({ top: 0, left: 0 });

    const handleMouseEnter = (e) => {
        const rect = e.currentTarget.getBoundingClientRect();
        setTooltipPos({
            top: rect.bottom + 8,
            left: rect.left
        });
        setShowTooltip(true);
    };

    return (
        <div style={{ position: "relative", display: "inline-block" }}>
            <span
                onMouseEnter={handleMouseEnter}
                onMouseLeave={() => setShowTooltip(false)}
                style={{
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: size,
                    height: size,
                    cursor: "help",
                    color: "#1976d2",
                    fontSize: size - 2,
                    marginLeft: 8
                }}
                title={tooltip}
            >
                ⓘ
            </span>
            {showTooltip && (
                <div style={{
                    position: "fixed",
                    top: tooltipPos.top,
                    left: tooltipPos.left,
                    background: "#333",
                    color: "#fff",
                    padding: "8px 12px",
                    borderRadius: 4,
                    fontSize: 12,
                    maxWidth: 250,
                    zIndex: 10000,
                    boxShadow: "0 2px 8px rgba(0,0,0,0.3)",
                    pointerEvents: "none",
                    wordWrap: "break-word",
                    whiteSpace: "normal"
                }}>
                    {tooltip}
                </div>
            )}
        </div>
    );
};

function formatCurrency(value) {
    if (value === null || value === undefined || isNaN(value)) return "$0.00";
    const numValue = Number(value);
    const isNegative = numValue < 0;
    const formattedValue = "$" + Math.abs(numValue).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

    return (
        <span style={{ color: isNegative ? "#e63946" : "inherit" }}>
            {isNegative ? "-" : ""}{formattedValue}
        </span>
    );
}


// --- Toggle Switch Component ---
function ToggleSwitch({ checked, onChange, label, description, isOrange = false, tooltip = "" }) {
    return (
        <div style={{ marginBottom: 16 }}>
            <label style={{
                display: "inline-flex",
                alignItems: "center",
                cursor: "pointer",
                width: "100%"
            }}>
                <span style={{ marginRight: 12, fontWeight: 600, minWidth: 200, display: "flex", alignItems: "center" }}>
                    {label}
                    {tooltip && <TooltipIcon tooltip={tooltip} />}
                </span>
                <span style={{
                    position: "relative",
                    display: "inline-block",
                    width: 44,
                    height: 24,
                    marginRight: 12
                }}>
                    <input
                        type="checkbox"
                        checked={checked}
                        onChange={e => onChange(e.target.checked)}
                        style={{ opacity: 0, width: 0, height: 0 }}
                    />
                    <span style={{
                        position: "absolute",
                        cursor: "pointer",
                        top: 0, left: 0, right: 0, bottom: 0,
                        background: checked ? (isOrange ? "#ff9800" : "#1976d2") : "#888",
                        borderRadius: 24,
                        transition: "background 0.2s"
                    }} />
                    <span style={{
                        position: "absolute",
                        left: checked ? 22 : 2,
                        top: 2,
                        width: 20,
                        height: 20,
                        background: "#fff",
                        borderRadius: "50%",
                        transition: "left 0.2s"
                    }} />
                </span>
            </label>
            {description && (
                <div style={{ color: "#aaa", fontSize: 11, marginTop: 4, marginLeft: 212 }}>
                    {description}
                </div>
            )}
        </div>
    );
}

export default function YTDPL({ userId = 1, refreshKey }) {
    const [ytdProfit, setYtdProfit] = useState(null);
    const [allTimeProfit, setAllTimeProfit] = useState(null);
    const [ytdSummary, setYtdSummary] = useState("");
    const [allTimeSummary, setAllTimeSummary] = useState("");
    const [optionsProfit, setOptionsProfit] = useState(null);
    const [tooltip, setTooltip] = useState("Year-to-date profit/loss summary");
    const [error, setError] = useState("");
    const [displayMode, setDisplayMode] = useState(() => sessionStorage.getItem(STORAGE_KEY) || "all_time");
    const [showOptionsProfit, setShowOptionsProfit] = useState(() => {
        const stored = sessionStorage.getItem(OPTIONS_PROFIT_KEY);
        // Default to true if not explicitly set to "false"
        return stored === null ? true : stored === "true";
    });
    const [showSettingsModal, setShowSettingsModal] = useState(false);
    const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
    const [privacyAvailable, setPrivacyAvailable] = useState(false);
    const [privacyEnabled, setPrivacyEnabled] = useState(() => sessionStorage.getItem(PRIVACY_MODE_KEY) === "true");
    const [userKey, setUserKey] = useState(() => sessionStorage.getItem(USER_KEY_STORAGE) || "");

    // Fetch YTD/All-time data
    useEffect(() => {
        axios.get(`${process.env.REACT_APP_API_URL}/api/ytd-pl`, {
            params: { refreshKey },
            withCredentials: true
        })
            .then(res => {
                setYtdProfit(res.data.ytd_profit);
                setAllTimeProfit(res.data.all_time_profit);
                setYtdSummary(res.data.ytd_summary || "💰 $0.00");
                setAllTimeSummary(res.data.all_time_summary || "💩 $0.00");
                setTooltip(res.data.tooltip);
                setCurrentYear(res.data.current_year);
                setError("");
                setPrivacyAvailable(res.data.privacy_available || false);
            })
            .catch((err) => {
                setError("Could not load P/L data.");
                setYtdProfit(null);
                setAllTimeProfit(null);
            });
    }, [refreshKey]);

    // Fetch options P/L data
    useEffect(() => {
        axios.get(`${process.env.REACT_APP_API_URL}/api/options-trade-table`, {
            params: { 
                refreshKey,
                component: "_8_YTD_PL"
            },
            withCredentials: true
        })
            .then(res => {
                const optionsProfitAmount = res.data.total_options_pl || 0;
                setOptionsProfit(optionsProfitAmount);
                // Store the options profit amount in session storage
                sessionStorage.setItem("ytdpl_options_profit_amount", optionsProfitAmount);
            })
            .catch((err) => {
                if (printDebug) console.log("Could not load options P/L data.");
                setOptionsProfit(0);
                sessionStorage.setItem("ytdpl_options_profit_amount", 0);
            });
    }, [refreshKey]);

    // Store displayMode in sessionStorage whenever it changes
    useEffect(() => {
        sessionStorage.setItem(STORAGE_KEY, displayMode);
    }, [displayMode]);

    // Store options profit setting in sessionStorage whenever it changes
    useEffect(() => {
        sessionStorage.setItem(OPTIONS_PROFIT_KEY, showOptionsProfit);
        // Dispatch custom event for other components to listen with both flag and amount
        window.dispatchEvent(new CustomEvent("optionsProfitChanged", { 
            detail: { 
                checked: showOptionsProfit,
                amount: optionsProfit
            } 
        }));
    }, [showOptionsProfit, optionsProfit]);

    // Store privacy mode in sessionStorage whenever it changes
    useEffect(() => {
        sessionStorage.setItem(PRIVACY_MODE_KEY, privacyEnabled);
    }, [privacyEnabled]);

    // Toggle handler for all time / ytd
    const handleToggle = () => {
        setDisplayMode(prev => (prev === "ytd" ? "all_time" : "ytd"));
    };

    // Toggle handler for options profit
    const handleOptionsProfitChange = (checked) => {
        setShowOptionsProfit(checked);
    };

    // Toggle handler for privacy mode
    const handlePrivacyChange = (checked) => {
        if (checked) {
            // --- ENABLING PRIVACY ---
            if (!privacyAvailable) {
                alert("No encrypted records found.");
                setPrivacyEnabled(false);
                return;
            }

            const key = window.prompt("Enter your Secret Key to decode symbols:");
            if (!key || key.trim() === "") {
                setPrivacyEnabled(false);
                return;
            }

            // SUCCESS: Set both state and session
            setUserKey(key);
            sessionStorage.setItem(USER_KEY_STORAGE, key);
            setPrivacyEnabled(true);
            sessionStorage.setItem(PRIVACY_MODE_KEY, "true");

            if (Printdebug) console.log(`${debugprefix} Privacy Mode ON. Key stored.`);
        } else {
            // --- DISABLING PRIVACY ---
            // 🛑 CRITICAL: Clear both states first
            setPrivacyEnabled(false);
            setUserKey("");

            // 🛑 CRITICAL: Remove from Session Storage
            sessionStorage.removeItem(USER_KEY_STORAGE);
            sessionStorage.setItem(PRIVACY_MODE_KEY, "false");

            if (Printdebug) console.log(`${debugprefix} Privacy Mode OFF. Key DELETED.`);
        }
    };

    // Replace your existing useEffect for privacy persistence with this:
    useEffect(() => {
        // Only store "true" if the key actually exists
        if (privacyEnabled && userKey) {
            sessionStorage.setItem(PRIVACY_MODE_KEY, "true");
        } else {
            sessionStorage.setItem(PRIVACY_MODE_KEY, "false");
            // Safety: if privacy is false, ensure key is gone
            if (!privacyEnabled) {
                sessionStorage.removeItem(USER_KEY_STORAGE);
            }
        }
    }, [privacyEnabled, userKey]);

    // Calculation helper for data masking and display
    const calculateDisplayValue = () => {
        // Show "Private" only if privacy is available AND privacy mode is OFF
        if (privacyAvailable && !privacyEnabled) {
            return "🔒 Private";
        }

        let baseProfit = isDisplayingAllTime ? allTimeProfit : ytdProfit;

        if (baseProfit === null || baseProfit === undefined) {
            return isDisplayingAllTime ? allTimeSummary : ytdSummary;
        }

        baseProfit = Number(baseProfit);

        if (showOptionsProfit && optionsProfit !== null && optionsProfit !== undefined && optionsProfit !== 0) {
            const total = baseProfit + Number(optionsProfit);
            return formatCurrency(total);
        }

        return isDisplayingAllTime ? allTimeSummary : ytdSummary;
    };

    // Determine what to display - Ensure displayValue is defined here
    const isDisplayingAllTime = displayMode === "all_time";
    const displayValue = calculateDisplayValue(); // Defined here for use in JSX
    const displayLabel = isDisplayingAllTime ? "All Time" : `YTD ${currentYear}`;

    const renderSettingsModal = () => {
        if (!showSettingsModal) return null;
        return (
            <>
                <div
                    style={{ position: "fixed", top: 0, left: 0, width: "100vw", height: "100vh", background: "rgba(24,26,37,0.35)", backdropFilter: "blur(2px)", zIndex: 9998 }}
                    onClick={() => setShowSettingsModal(false)}
                />
                <div style={{ position: "fixed", top: "50%", left: "50%", transform: "translate(-50%, -50%)", zIndex: 9999, minWidth: 500, minHeight: 300, background: "#181A25", borderRadius: 12, boxShadow: "0 2px 8px rgba(0,255,255,0.08)", padding: 24 }}>
                    <h3 style={{ marginTop: 0, marginBottom: 20, color: "#fff" }}>Display Settings</h3>

                    <ToggleSwitch
                        checked={isDisplayingAllTime}
                        onChange={handleToggle}
                        label="All Time"
                        description="Checked = All time. Uncheck = Year-to-date."
                        tooltip="Show all trading history or year-to-date only"
                    />

                    <ToggleSwitch
                        checked={showOptionsProfit}
                        onChange={handleOptionsProfitChange}
                        label="Options Profit"
                        description="Checked = Include options. Uncheck = Equities only."
                        isOrange={true}
                        tooltip="Includes credits from collected premiums for open positions"
                    />

                    {/* Updated Privacy Mode Toggle - Removed input box dependency */}
                    <ToggleSwitch
                        checked={privacyEnabled}
                        onChange={handlePrivacyChange}
                        label="Privacy Mode"
                        description={privacyAvailable ? "Decodes encrypted symbols. Incorrect symbols will appear with the wrong key." : "Disabled: No encrypted records found."}
                        isOrange={true}
                        tooltip="This option is only available because you uploaded a file with Privacy enabled."
                    />

                    <button
                        onClick={() => setShowSettingsModal(false)}
                        style={{ position: "absolute", top: 12, right: 18, background: "none", border: "none", color: "#fff", fontSize: 22, cursor: "pointer" }}
                    >
                        ✕
                    </button>
                </div>
            </>
        );
    };

    if (error) {
        return (
            <div className="ytdpl-container" style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ color: "#e63946", fontSize: 13 }}>{error}</span>
            </div>
        );
    }

    return (
        <div
            className="ytdpl-container"
            style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                padding: "4px 12px",
                borderRadius: 4,
                background: "transparent",
                transition: "all 0.2s ease"
            }}
            title={tooltip}
        >
            {/* Display Value */}
            <div
                style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 4,
                    cursor: "pointer",
                    userSelect: "none"
                }}
                onClick={handleToggle}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                        handleToggle();
                    }
                }}
            >
                <span style={{ fontSize: 14, fontWeight: 600 }} id="YTDnumber">
                    {displayValue}
                </span>
                <span style={{ fontSize: 11, color: "#999", fontWeight: 500 }}>
                    {displayLabel}
                </span>
            </div>

            {/* Settings Button */}
            <button
                onClick={() => setShowSettingsModal(true)}
                title="Settings"
                style={{
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    padding: "2px 4px",
                    color: "#fff",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 16,
                    transition: "opacity 0.2s ease"
                }}
                aria-label="Settings"
                tabIndex={0}
            >
                ⚙️
            </button>

            {renderSettingsModal()}

            {/* Info Icon */}
            <span
                style={{
                    marginLeft: 4,
                    fontSize: 14,
                    opacity: 0.7,
                    cursor: "help"
                }}
                title={tooltip}
            >
                🛈
            </span>
        </div>
    );
}